import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { agentsData, agentLimitMessage } from "Stores/agentsData";
import { toasterState } from "Components/common/toaster";
import AgentsCrumb from "./AgentsCrumb";
import AgentSelect from "./AgentSelect";
import DayOfWeekField from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";
import SignalIcon from "../signals/SignalIcon";

/*
 * The agent setup page: "the timeline is the form". Every /agents/new/<template> page renders through
 * SetupPage, so they all read the same way as the Signals "New agent" drawer: what starts it, then
 * numbered steps (each with its own picker), then a fixed bar with the cost, Cancel and Create agent.
 * Pages keep their own state, validation and payloads; this file is layout only.
 */

/** A number field's value as sent: blank or out of range falls back / clamps instead of sending 0. */
export const clampInt = (v, min, max, fallback) => {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max == null ? n : max, Math.max(min, n));
};

/** "Customize steps": open the template's composed flow in the builder, or say why it couldn't. */
export const openInBuilder = async (history, key, payload, setSaving) => {
  if (setSaving) setSaving(true);
  const draft = await agentsData.composeTemplate(key, payload);
  if (setSaving) setSaving(false);
  if (!draft) {
    toasterState.setToastMsg(agentsData.error || "Couldn't open the steps right now. Try again.", "failure");
    return;
  }
  agentsData.stageDraft(draft);
  history.push("/agents/new/custom");
};

const atLimit = usage => !!(usage && usage.enabled && usage.used >= usage.eligible);

export const SetupPage = observer(({ title, sub, back, name, children, bar }) => {
  // A page opened straight from its address skips the chooser's limit check: check here too.
  // A failed usage check must never break the page: then the server still refuses at create.
  useEffect(() => { Promise.resolve().then(() => agentsData.fetchUsage()).catch(() => {}); }, []);
  const blocked = atLimit(agentsData.usage);
  const { canSubmit, saving, onCreate, createLabel = "Create agent", onCustomize, customizeDisabled, onCancel, cost, reason } = bar;
  const why = blocked ? null : !canSubmit ? reason : null;
  // The suggested name is a real value until the person edits it (a placeholder looked filled but wasn't).
  const [nameTouched, setNameTouched] = useState(false);
  const shownName = name ? (nameTouched ? name.value : name.value || name.placeholder || "") : "";
  return (
    <div className="ag-page ag-sf">
      <div className="ag-page__head">
        <div>
          {back || <AgentsCrumb items={[{ label: "Hire an agent", to: "/agents/new" }, { label: "Set up" }]} />}
          <h1 className="ag-page__title">{title}</h1>
          {sub && <p className="ag-sf__sub">{sub}</p>}
        </div>
      </div>
      <div className="ag-sf__col">
        {blocked && <div className="ag-limit">{agentLimitMessage(agentsData.usage)}</div>}
        {name && (
          <label className="ag-sf__name">
            <span>Agent name</span>
            <input className="ag-input" value={shownName} onChange={e => { setNameTouched(true); name.onChange(e.target.value); }} />
          </label>
        )}
        <ol className="ag-sf__tl">{children}</ol>
      </div>
      <div className="ag-sf__bar">
        <div className="ag-sf__barin">
          <div className="ag-sf__cost">
            {cost && <span>{cost}</span>}
            {why && <span className="ag-sf__why">{why}</span>}
          </div>
          <button className="ag-btn" onClick={onCancel}>Cancel</button>
          {onCustomize && <button className="ag-btn" disabled={saving || customizeDisabled} onClick={onCustomize}><i className="material-icons-outlined">tune</i>Customize steps</button>}
          <button className="ag-btn ag-btn--primary" disabled={!canSubmit || saving || blocked} onClick={onCreate}>
            {saving ? "Creating…" : createLabel}
          </button>
        </div>
      </div>
    </div>
  );
});

/** What starts the agent: the timeline's first node, marked with the trigger's icon instead of a number. */
export const When = ({ icon = "clock", title, children }) => (
  <li className="ag-sf__item ag-sf__item--when">
    <span className="ag-sf__dot ag-sf__dot--when"><SignalIcon id={icon} size={16} /></span>
    <div className="ag-sf__card">
      <span className="ag-sf__eyebrow">When</span>
      {title && <b className="ag-sf__title">{title}</b>}
      {children}
    </div>
  </li>
);

/** One numbered step: icon tile, plain name, optional tags, then its pickers. Numbering is CSS. */
export const Step = ({ icon, tone = "violet", title, tags, children }) => (
  <li className="ag-sf__item">
    <span className="ag-sf__dot" aria-hidden="true" />
    <div className="ag-sf__card ag-sf__card--step">
      <span className={`ag-sf__tile is-${tone}`}><SignalIcon id={icon} size={18} /></span>
      <div className="ag-sf__main">
        <div className="ag-sf__name-row">
          <b className="ag-sf__title">{title}</b>
          {(tags || []).filter(Boolean).map(t => <span key={t.label} className={`ag-sf__tag${t.ask ? " is-ask" : ""}`}>{t.label}</span>)}
        </div>
        {children}
      </div>
    </div>
  </li>
);

/** A labelled field inside a card; `row` lays several side by side. */
// A div, not a <label>: a label forwards clicks on a picker's options to its search box.
export const Field = ({ label, children, hint, warn }) => (
  <div className="ag-sf__field">
    {label && <span className="ag-sf__label">{label}</span>}
    {children}
    {hint && <span className={`ag-sf__hint${warn ? " is-warn" : ""}`}>{hint}</span>}
  </div>
);
export const Row = ({ children }) => <div className="ag-sf__row">{children}</div>;

/** Run / days / time for a scheduled agent, and the time zone line. `manual` adds "Only when I run it". */
export const Schedule = ({ runWhen, setRunWhen, days, setDays, time, setTime, timezone, manual = true, options }) => (
  <>
    <Row>
      <Field label="Runs">
        <AgentSelect value={runWhen} onChange={setRunWhen} options={options || [
          { value: "DAILY", label: "Every day" },
          { value: "WEEKDAYS", label: "Weekdays (Mon–Fri)" },
          { value: "WEEKLY", label: "Specific days" },
          ...(manual ? [{ value: "MANUAL", label: "Only when I run it" }] : [])
        ]} />
      </Field>
      {runWhen !== "MANUAL" && (
        <Field label="Time">
          <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
        </Field>
      )}
    </Row>
    {runWhen === "WEEKLY" && <DayOfWeekField value={days} onChange={setDays} />}
    {runWhen !== "MANUAL" && <span className="ag-sf__hint">Your time zone: {String(timezone).replace(/_/g, " ")}</span>}
  </>
);

/** The sequence picker as a field: opens the shared folder-aware modal. `onClear` makes it optional. */
export const SequenceField = ({ id, name, onPick, onClear, placeholder = "Pick a sequence", clearLabel = "Clear" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`ag-select__btn${id || onClear ? "" : " ag-sf__need"}`} onClick={() => setOpen(true)}>
        <span className={name ? "" : "ag-select__ph"}>{name || placeholder}</span>
        <SignalIcon id="chevron" size={16} />
      </button>
      {onClear && id && <button type="button" className="ag-link ag-sf__clear" onClick={onClear}>{clearLabel}</button>}
      <SequencePickerModal open={open} currentId={id} onSelect={s => onPick(s)} onClose={() => setOpen(false)} />
    </>
  );
};

const NOTIFY_OPTIONS = [
  { value: "DAILY_DIGEST", label: "Email · daily digest" },
  { value: "ON_FAILURE", label: "Email · only when a run fails" },
  { value: "OFF", label: "Off" }
];

/** The last step: how the agent tells you. */
export const NotifyStep = ({ value, onChange, title = "Tell me" }) => (
  <Step icon="bell" tone="grey" title={title}>
    <AgentSelect value={value} onChange={onChange} options={NOTIFY_OPTIONS} />
  </Step>
);

/** The fixed "already handled / do-not-contact" line under the steps. */
export const SkipLine = ({ children = "Skips anyone already handled, on do-not-contact or undeliverable." }) => (
  <li className="ag-sf__skip"><SignalIcon id="shield" size={14} />{children}</li>
);

/** Marks a required picker amber until it has a value, like the empty sequence picker. */
export const Need = ({ ok, children }) => <div className={ok ? "" : "ag-sf__needwrap"}>{children}</div>;

/**
 * A split in the flow: the card asks the question, then one row per outcome inside it ("If yes →", "If no →"),
 * each holding that path's steps or "Nothing extra". The step after it runs for everyone.
 * rows: [{ label, body }].
 */
export const Branch = ({ icon = "split", title, children, rows }) => (
  <li className="ag-sf__item af-branch">
    <span className="ag-sf__dot" aria-hidden="true" />
    <div className="ag-sf__card ag-sf__card--step">
      <span className="ag-sf__tile is-violet"><SignalIcon id={icon} size={18} /></span>
      <div className="ag-sf__main">
        <div className="ag-sf__name-row"><b className="ag-sf__title">{title}</b></div>
        {children}
        <OutcomeRows rows={rows} />
      </div>
    </div>
  </li>
);

/** The outcome rows of a split (shared with the builder): "If … →" on the left, that path's steps on the right. */
export const OutcomeRows = ({ rows }) => (
  <div className="af-rows">
    {rows.map(r => (
      <div className="af-row" key={r.label}>
        <div className="af-row__label">{r.label}<span aria-hidden="true">→</span></div>
        <div className="af-row__body">{r.body || <span className="af-row__none">Nothing extra</span>}</div>
      </div>
    ))}
  </div>
);
